import { useCallback, useEffect, useState } from 'react'

import { supportsScreenSharing } from '@livekit/components-core'
import { useLocalParticipant, usePersistentUserChoices, useTracks } from '@livekit/components-react'
import { Track } from 'livekit-client'

import i18n from 'modules/core/lib/i18n'

import { ControlBar } from 'modules/meetings/components/ControlBar'
import { useRoomContext } from 'modules/meetings/lib/hooks/useRoomContext'

import { SettingsModal } from './SettingsModal'

interface RoomControlBarProps {
  handleDisconnect: () => void
}

export const RoomControlBar = ({ handleDisconnect }: RoomControlBarProps) => {
  const { isSideBarOpen, isChatOpen, setIsChatOpen, setIsSideBarOpen, pendingMessagesCount } =
    useRoomContext()
  const { userChoices, saveAudioInputEnabled, saveVideoInputEnabled } = usePersistentUserChoices()
  const { localParticipant } = useLocalParticipant()
  const [openModal, setOpenModal] = useState(false)
  const [screenShareEnabled, setScreenShareEnabled] = useState(false)

  const browserSupportsScreenSharing = supportsScreenSharing()

  const [audioEnabled, setAudioEnabled] = useState(userChoices.audioEnabled)
  const [videoEnabled, setVideoEnabled] = useState(userChoices.videoEnabled)

  // Detect if screen sharing is currently active
  const screenShareTracks = useTracks(
    [{ source: Track.Source.ScreenShare, withPlaceholder: false }],
    { onlySubscribed: false }
  )

  // Check if local participant has an active screen share track
  const isScreenSharing = screenShareTracks.some(
    (track) => track.participant.isLocal && track.publication?.isEnabled
  )

  // Save settings when they change (similar to prejoin page)
  useEffect(() => {
    saveAudioInputEnabled(audioEnabled)
    saveVideoInputEnabled(videoEnabled)
  }, [audioEnabled, videoEnabled, saveAudioInputEnabled, saveVideoInputEnabled])

  const handleMicToggle = useCallback(async () => {
    try {
      const newState = !audioEnabled
      await localParticipant.setMicrophoneEnabled(newState)
      setAudioEnabled(newState)
    } catch (error) {}
  }, [audioEnabled, localParticipant])

  const handleCameraToggle = useCallback(async () => {
    try {
      const newState = !videoEnabled
      await localParticipant.setCameraEnabled(newState)
      setVideoEnabled(newState)
    } catch (error) {}
  }, [videoEnabled, localParticipant])

  const handleScreenShareToggle = useCallback(async () => {
    try {
      if (screenShareEnabled) {
        await localParticipant.setScreenShareEnabled(false)
      } else {
        await localParticipant.setScreenShareEnabled(true, {
          audio: false,
          selfBrowserSurface: 'include',
        })
      }
      setScreenShareEnabled(!screenShareEnabled)
    } catch (error) {}
  }, [screenShareEnabled, localParticipant])

  return (
    <>
      <ControlBar
        handleMicToggle={handleMicToggle}
        handleCameraToggle={handleCameraToggle}
        handleScreenShareToggle={handleScreenShareToggle}
        handleDisconnect={handleDisconnect}
        audioEnabled={audioEnabled}
        videoEnabled={videoEnabled}
        screenShareEnabled={screenShareEnabled}
        browserSupportsScreenSharing={browserSupportsScreenSharing}
        onOpenSettings={() => setOpenModal(true)}
        labels={{
          microphone: i18n.t('meetings.room.control_bar.microphone.label'),
          camera: i18n.t('meetings.room.control_bar.camera.label'),
          screenShare: screenShareEnabled
            ? i18n.t('meetings.room.control_bar.screen_share.stop')
            : i18n.t('meetings.room.control_bar.screen_share.start'),
          settings: i18n.t('meetings.room.control_bar.settings'),
          exit: i18n.t('meetings.room.control_bar.exit'),
          toggleSideBar: isSideBarOpen
            ? i18n.t('meetings.room.control_bar.sidebar.close')
            : i18n.t('meetings.room.control_bar.sidebar.open'),
          toggleChat: isChatOpen
            ? i18n.t('meetings.room.control_bar.chat.close')
            : i18n.t('meetings.room.control_bar.chat.open'),
        }}
        isSideBarOpen={isSideBarOpen}
        setIsSideBarOpen={setIsSideBarOpen}
        isChatOpen={isChatOpen}
        setIsChatOpen={setIsChatOpen}
        isScreenSharing={isScreenSharing}
        pendingMessagesCount={pendingMessagesCount}
      />

      <SettingsModal
        isOpen={openModal}
        onClose={() => setOpenModal(false)}
        audioEnabled={audioEnabled}
        videoEnabled={videoEnabled}
        setAudioEnabled={setAudioEnabled}
        setVideoEnabled={setVideoEnabled}
      />
    </>
  )
}
